// src/App.js — routing, state and auth only
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import * as api from "./apiService";
import HotMelt from "./HotMelt";
import logoImg from "./Al_Mehraj_Group.png";
import strongBondImg from "./Strong-Bond.png";
import "./App.css";

import { useGoogleAuth }                  from "./hooks/useGoogleAuth";
import { NAV } from "./navConfig";
import { ToastProvider } from "./components/Toast";
import { normFormula, groupFormulas,
         sfgCostPerKg, fgContentCostPerKg,
         packCostBreakdown }              from "./utils/costingUtils";

import { PageDashboard, PageCostingDashboard, PageLogisticsDashboard,
         PageProductionDashboard, PageMaintenanceDashboard,
         PageAdminDashboard, PageQCDashboard, PageRnDDashboard, PageTotalEnergiesDashboard,
         PageAccountsDashboard, PageHRDashboard,
         DEPT_CONFIG, ROLE_PRIORITY }            from "./pages/dashboard/PageDashboard";
import { PageSettings }                        from "./pages/costing/PageSettings";
import { PageRM, AddRMModal, RMHistModal }      from "./pages/costing/PageRM";
import { PageSFG, AddSFGModal, EditSFGModal,
         SFGFormModal }                         from "./pages/costing/PageSFG";
import { PageFG, FGFormModal, CostCellModal,
         AddFGModal, EditFGModal }              from "./pages/costing/PageFG";
import { PageHistory }                         from "./pages/costing/PageHistory";
import { PageWbPriceSnapshot }                 from "./pages/costing/PageWbPriceSnapshot";
import { PageLargeOrderCosting }              from "./pages/costing/PageLargeOrderCosting";
import { PageQCFgSpecs }                       from "./pages/qc/PageQCFgSpecs";
import { PageQCRmSpecs }                       from "./pages/qc/PageQCRmSpecs";
import { PageQCDailyAnalysis }                 from "./pages/qc/PageQCDailyAnalysis";
import { PageQCRmAnalysis }                   from "./pages/qc/PageQCRmAnalysis";
import { PageQCHmSpecs }                     from "./pages/qc/PageQCHmSpecs";
import { PageQCHmAnalysis }                 from "./pages/qc/PageQCHmAnalysis";
import { FgSpecModal }                         from "./pages/qc/FgSpecModal";
import { RmSpecParamModal }                    from "./pages/qc/RmSpecParamModal";
import { PageAuditLog }                        from "./pages/admin/PageAuditLog";
import { PageInfraKeys }                       from "./pages/admin/PageInfraKeys";
import { PageMixerConfig }                     from "./pages/admin/PageMixerConfig";
import { PageUsers }                           from "./pages/admin/PageUsers";
import { PageSystemHealth }                    from "./pages/admin/PageSystemHealth";
import { PageCrmGroups }                       from "./pages/admin/PageCrmGroups";
import { PageBankAccounts }                    from "./pages/admin/PageBankAccounts";
import { PageCrmAccounts }                     from "./pages/crm/PageCrmAccounts";
import { PageCrmAccountDetail }               from "./pages/crm/PageCrmAccountDetail";
import { PageCrmPipeline }                    from "./pages/crm/PageCrmPipeline";
import { PageCrmSamples }                     from "./pages/crm/PageCrmSamples";
import { PageRMCalc }                          from "./pages/logistics/PageRMCalc";
import { PageSalesDashboard, PageSalesWaterBased,
         PageSalesHotMelt,
         PageProductCatalogDashboard }         from "./pages/sales/PageSales";
import { PageWorkOrders }                      from "./pages/sales/PageWorkOrders";
import { PageQuotations }                      from "./pages/sales/PageQuotations";
import { PageProformaInvoices }               from "./pages/sales/PageProformaInvoices";
import { PageVisitReports }                   from "./pages/sales/PageVisitReports";
import { PageMonthlyReports }                from "./pages/sales/PageMonthlyReports";
import { PageSalesTargets }                 from "./pages/sales/PageSalesTargets";
import { PageSalesCustomerSampleSubmissions } from "./pages/sales/PageSalesCustomerSampleSubmissions";
import { PageSOAPaymentFollowUp }           from "./pages/sales/PageSOAPaymentFollowUp";
import { PageOrderBook }                    from "./pages/sales/PageOrderBook";
import { PageProductionPlan }              from "./pages/logistics/PageProductionPlan";
import { PageDeliveryPlan }               from "./pages/logistics/PageDeliveryPlan";
import { PageMyTasks }                         from "./pages/tasks/PageMyTasks";
import { PageQCCustomerSamples }               from "./pages/qc/PageQCCustomerSamples";
import { PageQCCoolantSpecs }                  from "./pages/qc/PageQCCoolantSpecs";
import { PageQCCoolantAnalysis }               from "./pages/qc/PageQCCoolantAnalysis";
import { PageQCWaterAnalysis }                from "./pages/qc/PageQCWaterAnalysis";
import { PageQCBfSpecs }                       from "./pages/qc/PageQCBfSpecs";
import { PageQCBfAnalysis }                    from "./pages/qc/PageQCBfAnalysis";
import { PageQCCoa }                           from "./pages/qc/PageQCCoa";
import { PageQCRmNc }                          from "./pages/qc/PageQCRmNc";
import { PageQCFgNc }                          from "./pages/qc/PageQCFgNc";
import { PageCustomerComplaints }              from "./pages/qc/PageCustomerComplaints";
import { PageRdEquipment }                     from "./pages/qc/PageRdEquipment";
import { PageTds }                             from "./pages/qc/PageTds";
import { PageMsds }                            from "./pages/qc/PageMsds";
import { Watermark }                           from "./components/Watermark";
import { PageRdCalibrations }                  from "./pages/qc/PageRdCalibrations";
import { PageRdPipeline }                      from "./pages/qc/PageRdPipeline";
import { PageRdNpd }                           from "./pages/qc/PageRdNpd";
import { PageRdRmEvaluation }                  from "./pages/qc/PageRdRmEvaluation";

const CARD_SECTION_MAP = {
  "costing":    "Costing",
  "logistics":  "Logistics & Planning",
  "accounts":   "Accounts & Finances",
  "qc":         "Quality Control",
  "rnd":        "Research and Development",
  "sales":      "Sales",
  "production": "Production",
  "maintenance":"Maintenance",
  "admin-hub":  "Admin",
};


const SECTION_PAGE = {
  "Costing":              "costing",
  "Logistics & Planning": "logistics",
  "Accounts & Finances":  "accounts",
  "Quality Control":      "qc",
  "Research and Development":"rnd",
  "Sales":                "sales",
  "Production":           "production",
  "Maintenance":          "maintenance",
  "Human Resources":      "hr",
  "Admin":                "admin-hub",
};

export default function App() {
  const [page,        setPageRaw]    = useState("home");
  const [pageHistory, setPageHistory] = useState([]);

  function setPage(newPage) {
    setPageHistory(h => [...h, page]);
    setPageRaw(newPage);
  }

  function goBack() {
    if (pageHistory.length === 0) return;
    setPageRaw(pageHistory[pageHistory.length - 1]);
    setPageHistory(h => h.slice(0, -1));
  }
  const [isAuthed,   setIsAuthed]   = useState(false);
  const [userEmail,  setUserEmail]  = useState("");
  const [userName,   setUserName]   = useState("");
  const [myPerms,    setMyPerms]    = useState([]);
  const [myRoles,    setMyRoles]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [modal,      setModal]      = useState(null);
  const [idleWarning, setIdleWarning] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const [rmSearch,      setRMSearch]      = useState("");
  const [crmAccountId,  setCrmAccountId]  = useState(null);
  const [fgSearch,   setFGSearch]   = useState("");

  const [settings,    setSettings]    = useState({ labourPerKg:1.5, customPct:35, wastageGlobal:1, packing:[] });
  const [rms,         setRMs]         = useState([]);
  const [sfgs,        setSFGs]        = useState([]);
  const [sfgFormulas, setSFGFormulas] = useState({});
  const [fgs,         setFGs]         = useState([]);
  const [fgFormulas,  setFGFormulas]  = useState({});
  const [priceHist,   setPriceHist]   = useState([]);
  const [hmGrades,    setHmGrades]    = useState([]);
  const [crmCompanies, setCrmCompanies] = useState([]);

  const handleToken = useCallback(async (token) => {
    localStorage.setItem("id_token", token);
    setIsAuthed(true);
  }, []);

  const { ready: gisReady, signIn } = useGoogleAuth(handleToken);

  const loadAll = useCallback(async () => {
    if (!isAuthed) return;
    setLoading(true); setError(null);
    try {
      const [meData, rmsData, rmPrices, sfgsData, fgsData, settingsData, histData, allSfgForms, allFgForms, hmGradesData, crmCompaniesData] = await Promise.all([
        api.fetchMe(),
        api.fetchRMs().catch(() => []),
        api.fetchRMPrices().catch(() => ({})),
        api.fetchSFGs().catch(() => []),
        api.fetchFGs().catch(() => []),
        api.fetchSettings().catch(() => ({ labourPerKg:1.5, customPct:35, wastageGlobal:1, packing:[] })),
        api.fetchPriceHistory().catch(() => []),
        api.fetchAllSFGFormulas().catch(() => []),
        api.fetchAllFGFormulas().catch(() => []),
        api.fetchHmGrades().catch(() => []),
        api.fetchCrmCompanies().catch(() => []),
      ]);
      setUserEmail(meData.email);
      setUserName(meData.name || meData.email);
      setMyPerms(meData.permissions || []);
      setMyRoles(meData.roles || []);
      setRMs(rmsData.map(rm => ({ ...rm, unit:rm.uom, price:Number(rmPrices[rm.id] ?? 0) })).sort((a,b) => a.name.localeCompare(b.name)));
      setSFGs([...sfgsData].sort((a,b) => a.name.localeCompare(b.name)));
      setSFGFormulas(groupFormulas(allSfgForms));
      setFGs([...fgsData].sort((a,b) => a.name.localeCompare(b.name)));
      setFGFormulas(groupFormulas(allFgForms));
      setSettings(settingsData);
      setPriceHist(histData);
      setHmGrades(hmGradesData);
      setCrmCompanies(crmCompaniesData);
    } catch (e) {
      if (e.message.includes("401") || e.message.includes("403")) {
        localStorage.removeItem("id_token");
        setIsAuthed(false);
      } else {
        setError(e.message);
      }
    } finally { setLoading(false); }
  }, [isAuthed]);

  useEffect(() => {
    const token = localStorage.getItem("id_token");
    if (token) setIsAuthed(true);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Idle auto-logout ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthed) { setIdleWarning(false); return; }

    const WARN_MS  = 14 * 60 * 1000;   // show warning at 14 min
    const LOGOUT_MS = 15 * 60 * 1000;  // sign out at 15 min

    function resetTimer() { lastActivityRef.current = Date.now(); }

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));

    const interval = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= LOGOUT_MS) {
        setIdleWarning(false);
        signOut();
      } else if (idle >= WARN_MS) {
        setIdleWarning(true);
      } else {
        setIdleWarning(false);
      }
    }, 30_000);  // check every 30 s

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearInterval(interval);
    };
  }, [isAuthed]);

  const sfgCost   = useCallback(id => sfgCostPerKg(id, sfgFormulas, rms), [sfgFormulas, rms]);
  const fgContent = useCallback(id => fgContentCostPerKg(id, fgFormulas, sfgFormulas, rms), [fgFormulas, sfgFormulas, rms]);
  const packBreak = useCallback(
    (fg, pkg) => packCostBreakdown(fg, pkg, fgFormulas, sfgFormulas, rms, settings.labourPerKg, settings.wastageGlobal ?? 1),
    [fgFormulas, sfgFormulas, rms, settings.labourPerKg, settings.wastageGlobal]
  );

  async function handleSettingsChange(next) {
    setSettings(next);
    try { await api.updateSettings(next); }
    catch (e) { alert("Settings save failed: " + e.message); }
  }
  async function handlePackingPrice(pkgId, newPrice) {
    const newPacking = settings.packing.map(p => p.id===pkgId ? {...p, price:newPrice} : p);
    const next = {...settings, packing:newPacking};
    setSettings(next);
    try { await api.updateSettings(next); }
    catch (e) { alert("Settings save failed: " + e.message); }
  }
  async function handleRMPriceUpdate(rmId, newPrice, note="") {
    const rm = rms.find(r => r.id===rmId);
    if (!rm || rm.price===newPrice) return;
    setRMs(old => old.map(r => r.id===rmId ? {...r, price:newPrice} : r));
    try {
      await api.updateRMPrice(rmId, { price:newPrice, note });
      const hist = await api.fetchPriceHistory();
      setPriceHist(hist);
    } catch {
      setRMs(old => old.map(r => r.id===rmId ? rm : r));
    }
  }

  async function handleAddRM(form) {
    const rm = await api.createRM({ id:form.id.trim(), name:form.name.trim(), uom:form.unit||"kg" });
    const price = parseFloat(form.price);
    if (!isNaN(price) && price > 0) {
      await api.updateRMPrice(rm.id, { price, note:"Initial price" });
    }
    setRMs(old => [...old, {...rm, unit:rm.uom, price:(!isNaN(price)&&price>0)?price:0}].sort((a,b) => a.name.localeCompare(b.name)));
    setModal({ type:"addRMSpec", data:rm });
  }
  async function handleAddRMSpec(rmId, paramForm) {
    await api.addRmSpecParam(rmId, paramForm);
    setModal(null);
  }

  async function handleAddSFG(form) {
    const sfg = await api.createSFG({ id:form.id||form.name, name:form.name, uom:form.unit||"kg" });
    setSFGs(old => [...old, sfg].sort((a,b) => a.name.localeCompare(b.name)));
    setSFGFormulas(old => ({...old, [sfg.id]:[]}));
    setModal({ type:"addSFGSpec", data:sfg });
  }
  async function handleAddSFGSpec(fgId, specForm) {
    await api.saveFgSpec(fgId, specForm);
    setModal(null);
  }
  async function handleSaveSFGFormula(sfgId, lines) {
    const saved = await api.saveSFGFormula(sfgId, lines);
    setSFGFormulas(old => ({...old, [sfgId]:saved.map(normFormula)}));
    setModal(null);
  }

  async function handleAddFG(form) {
    const fg = await api.createFG({ id:form.customId?.trim(), name:form.name });
    setFGs(old => [...old, fg].sort((a,b) => a.name.localeCompare(b.name)));
    setFGFormulas(old => ({...old, [fg.id]:[]}));
    setModal({ type:"addFGSpec", data:fg });
  }
  async function handleAddFGSpec(fgId, specForm) {
    await api.saveFgSpec(fgId, specForm);
    setModal(null);
  }
  async function handleSaveFGFormula(fgId, lines) {
    const saved = await api.saveFGFormula(fgId, lines);
    setFGFormulas(old => ({...old, [fgId]:saved.map(normFormula)}));
    setModal(null);
  }

  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  // Block Ctrl+P / Cmd+P — CSS @media print hides body as a second layer of defence
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, []);

  const filteredRMs = useMemo(() =>
    rms.filter(r => r.name.toLowerCase().includes(rmSearch.toLowerCase()) || r.id.toLowerCase().includes(rmSearch.toLowerCase())),
    [rms, rmSearch]);


  const visibleNav = useMemo(() => {
    if (!isAuthed) return [];
    const primaryRole   = ROLE_PRIORITY.find(r => myRoles.includes(r)) ?? "STORES";
    const cards         = DEPT_CONFIG[primaryRole]?.cards ?? [];
    const visibleSections = new Set(cards.map(c => CARD_SECTION_MAP[c.id]).filter(Boolean));
    return NAV.filter(({ section, perm }) =>
      (!section || visibleSections.has(section)) &&
      (!perm    || myPerms.includes(perm))
    );
  }, [isAuthed, myRoles, myPerms]);

  function signOut() {
    localStorage.removeItem("id_token");
    setIsAuthed(false);
    setMyPerms([]);
    setMyRoles([]);
    setPageHistory([]);
    setPageRaw("home");
  }

  function renderNav(onItemClick) {
    let lastSection = null;
    return visibleNav.map(({ id, icon, label, section }) => {
      const showHeader = section && section !== lastSection;
      if (showHeader) lastSection = section;
      const sectionPage = section ? SECTION_PAGE[section] : null;
      return (
        <div key={id}>
          {showHeader && (
            <div
              className={`nav-section-header${sectionPage ? " nav-section-header--link" : ""}`}
              onClick={() => { if (sectionPage) { setPage(sectionPage); onItemClick?.(); } }}
            >
              {section}
            </div>
          )}
          <div
            className={`nav-item${page === id ? ' active' : ''}`}
            onClick={() => { setPage(id); onItemClick?.(); }}
          >
            {icon} {label}
          </div>
        </div>
      );
    });
  }

  if (!isAuthed) return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-logo">
          <img src={logoImg} alt="Al Mehraj" style={{ width:"100%", maxWidth:330, height:"auto", objectFit:"contain" }} />
        </div>
        <div className="login-title">Al Mehraj Internal App</div>
        <div className="login-subtitle">Sign in with your @almehrajadhesives.com account</div>
        <div className="login-divider" />
        <button className="login-google-btn" onClick={signIn} disabled={!gisReady}>
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          <span>{gisReady ? "Sign in with Google" : "Loading…"}</span>
        </button>
      </div>
      <div className="login-brand">
        <div className="login-brand-logo">
          <img src={strongBondImg} alt="Strong Bond Products" />
        </div>
        <span className="login-brand-text">by Al Mehraj Group</span>
      </div>
    </div>
  );

  return (
    <ToastProvider>
    <div className="app">
      {/* ── Mobile top bar ──────────────────────────── */}
      <div className="topbar">
        <button className="topbar-hamburger" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
          <span /><span /><span />
        </button>
        <img src={logoImg} alt="Al Mehraj" className="topbar-logo" />
        <div className="topbar-avatar">
          {(userName || '').split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U'}
        </div>
      </div>

      {/* ── Drawer overlay ──────────────────────────── */}
      {drawerOpen && (
        <div className="drawer-overlay" onClick={() => setDrawerOpen(false)} />
      )}

      {/* ── Hamburger drawer ────────────────────────── */}
      <div className={`drawer${drawerOpen ? ' drawer-open' : ''}`}>
        <div className="drawer-header">
          <img src={logoImg} alt="Al Mehraj" style={{ height:28 }} />
          <button className="drawer-close" onClick={() => setDrawerOpen(false)}>✕</button>
        </div>
        <div className="drawer-nav">
          {renderNav(() => setDrawerOpen(false))}
        </div>
        <div className="sidebar-footer">
          <div className="sidebar-user-name">● {userName}</div>
          <div className="sidebar-user-email">{userEmail}</div>
          <button className="btn btn-full" style={{ fontSize:11 }} onClick={() => { loadAll(); setDrawerOpen(false); }}>↺ Reload</button>
          <button className="btn btn-full" style={{ fontSize:11 }} onClick={signOut}>Sign out</button>
        </div>
      </div>

      <div className="sidebar">
        <div className="sidebar-logo">
          <img src={logoImg} alt="Al Mehraj" style={{ height:52, width:"auto", objectFit:"contain", maxWidth:192 }} />
        </div>
        <div className="sidebar-nav">{renderNav()}</div>
        <div className="sidebar-footer">
          <div className="sidebar-user-name">● {userName}</div>
          <div className="sidebar-user-email">{userEmail}</div>
          <button className="btn btn-full" style={{ fontSize:11 }} onClick={loadAll}>↺ Reload</button>
          <button className="btn btn-full" style={{ fontSize:11 }} onClick={signOut}>Sign out</button>
        </div>
      </div>

      <div className="main">
        {pageHistory.length > 0 && !["home","tasks","costing","logistics","accounts","admin-hub","qc","rnd","sales","product-catalog","production","maintenance","hr"].includes(page) && (
          <button className="btn" onClick={goBack}
            style={{ margin:"12px 0 4px", fontSize:12, padding:"4px 12px" }}>
            ← Back
          </button>
        )}
        {loading && <div style={{ padding:40, textAlign:"center", color:"#888" }}>Loading…</div>}
        {error   && <div className="alert-warn" style={{ padding:16 }}>Error: {error} <button className="btn" onClick={loadAll}>Retry</button></div>}
        {!loading && !error && <>
          {page==="home"      && <PageDashboard userName={userName} myRoles={myRoles} rms={rms} sfgs={sfgs} fgs={fgs} setPage={setPage} />}
          {page==="tasks"     && <PageMyTasks myPerms={myPerms} userEmail={userEmail} />}
          {page==="settings"  && <PageSettings settings={settings} onSettingsChange={handleSettingsChange} onPackingPrice={handlePackingPrice} />}
          {page==="rm"        && <PageRM rms={filteredRMs} search={rmSearch} setSearch={setRMSearch} sfgs={sfgs} sfgFormulas={sfgFormulas} fgs={fgs} fgFormulas={fgFormulas} priceHist={priceHist} onUpdate={handleRMPriceUpdate} onAdd={() => setModal({type:"addRM"})} onHistory={rm => setModal({type:"rmHist", data:rm})} />}
          {page==="sfg"       && <PageSFG sfgs={sfgs} sfgFormulas={sfgFormulas} rms={rms} sfgCost={sfgCost} fgs={fgs} fgFormulas={fgFormulas} onAdd={() => setModal({type:"addSFG"})} onSFGClick={sfg => setModal({type:"sfgForm", data:sfg})} onEdit={sfg => setModal({type:"editSFG", data:sfg})} />}
          {page==="fg"        && <PageFG fgs={fgs} rms={rms} sfgs={sfgs} fgFormulas={fgFormulas} sfgFormulas={sfgFormulas} settings={settings} fgContent={fgContent} packBreak={packBreak} onSettingsChange={handleSettingsChange} onPackingPrice={handlePackingPrice} search={fgSearch} setSearch={setFGSearch} onAdd={() => setModal({type:"addFG"})} onEdit={fg => setModal({type:"editFG", data:fg})} onFGClick={fg => setModal({type:"fgForm", data:fg})} onCostCell={(fg,pkg) => setModal({type:"costCell", data:{fg,pkg}})} />}
          {page==="history"      && <PageHistory priceHist={priceHist} rms={rms} />}
          {page==="wb-snapshots"        && <PageWbPriceSnapshot />}
          {page==="large-order-costing" && <PageLargeOrderCosting fgs={fgs} fgFormulas={fgFormulas} sfgFormulas={sfgFormulas} rms={rms} settings={settings} hmGrades={hmGrades} />}
          {page==="hotmelt"             && <div style={{ padding:"0 0 24px" }}><HotMelt /></div>}
          {page==="auditlog"      && <PageAuditLog />}
          {page==="system-health" && <PageSystemHealth />}
          {page==="crm-accounts" && <PageCrmAccounts onSelectAccount={id => { setCrmAccountId(id); setPage("crm-account"); }} myPerms={myPerms} userEmail={userEmail} />}
          {page==="crm-account"  && <PageCrmAccountDetail key={crmAccountId} accountId={crmAccountId} onBack={goBack} myPerms={myPerms} />}
          {page==="sales-pipeline" && <PageCrmPipeline myPerms={myPerms} userEmail={userEmail} onBack={goBack} />}
          {page==="sample-trials"  && <PageCrmSamples  myPerms={myPerms} userEmail={userEmail} onBack={goBack} />}
          {page==="crm-groups"    && <PageCrmGroups />}
          {page==="costing"     && <PageCostingDashboard setPage={setPage} rms={rms} sfgs={sfgs} fgs={fgs} />}
          {page==="logistics"   && <PageLogisticsDashboard setPage={setPage} />}
          {page==="admin-hub"   && <PageAdminDashboard setPage={setPage} />}
          {page==="qc"          && <PageQCDashboard setPage={setPage} />}
          {page==="qc-fg-specs" && <PageQCFgSpecs fgs={fgs} sfgs={sfgs} />}
          {page==="qc-rm-specs" && <PageQCRmSpecs rms={rms} />}
          {page==="qc-analysis"    && <PageQCDailyAnalysis fgs={fgs} sfgs={sfgs} myPerms={myPerms} />}
          {page==="qc-customer-samples" && <PageQCCustomerSamples myPerms={myPerms} fgSpecs={[]} />}
          {page==="qc-coa"         && <PageQCCoa myPerms={myPerms} fgs={fgs} sfgs={sfgs} />}
          {page==="qc-coolant"     && <PageQCCoolantAnalysis myPerms={myPerms} />}
          {page==="qc-brake"       && <PageQCBfAnalysis myPerms={myPerms} />}
          {page==="qc-calibration"      && <PageRdCalibrations myPerms={myPerms} />}
          {page==="qc-tds"              && <PageTds myPerms={myPerms} fgs={fgs} />}
          {page==="qc-msds"             && <PageMsds myPerms={myPerms} fgs={fgs} />}
          {page==="qc-daily-water"      && <PageQCWaterAnalysis myPerms={myPerms} />}
          {page==="rnd"                 && <PageRnDDashboard setPage={setPage} />}
          {page==="qc-rd-pipeline"      && <PageRdPipeline myPerms={myPerms} />}
          {page==="rd-new-product"      && <PageRdNpd myPerms={myPerms} rms={rms} sfgs={sfgs} />}
          {page==="rd-rm-evaluation"    && <PageRdRmEvaluation rms={rms} myPerms={myPerms} />}
          {page==="rd-equipment"        && <PageRdEquipment myPerms={myPerms} />}
          {page==="qc-rm-nc"            && <PageQCRmNc myPerms={myPerms} />}
          {page==="qc-fg-nc"            && <PageQCFgNc myPerms={myPerms} />}
          {page==="customer-complaints" && <PageCustomerComplaints myPerms={myPerms} fgs={fgs} />}
          {page==="qc-hm-specs"       && <PageQCHmSpecs />}
          {page==="qc-totalenergies"  && <PageTotalEnergiesDashboard setPage={setPage} />}
          {page==="qc-coolant-specs"  && <PageQCCoolantSpecs myPerms={myPerms} />}
          {page==="qc-brake-specs"    && <PageQCBfSpecs myPerms={myPerms} />}
          {page==="qc-hm-analysis"    && <PageQCHmAnalysis myPerms={myPerms} />}
          {page==="qc-rm-analysis"    && <PageQCRmAnalysis rms={rms} myPerms={myPerms} />}
          {page==="order-book"        && <PageOrderBook myPerms={myPerms} />}
          {page==="prod-planning"     && <PageProductionPlan myPerms={myPerms} />}
          {page==="rm-reorder"        && <div className="alert-info" style={{ padding:24 }}>Raw Material Reorder — coming soon.</div>}
          {page==="delivery-planning" && <PageDeliveryPlan myPerms={myPerms} />}
          {page==="accounts"                  && <PageAccountsDashboard setPage={setPage} />}
          {page==="accounts-monthly-reports"  && <PageMonthlyReports myPerms={myPerms} userEmail={userEmail} />}
          {page==="accounts-weekly-report"    && <div className="alert-info" style={{ padding:24 }}>Weekly Report — coming soon.</div>}
          {page==="accounts-daybook"          && <div className="alert-info" style={{ padding:24 }}>Day Book — coming soon.</div>}
          {page==="sales"          && <PageSalesDashboard setPage={setPage} />}
          {page==="product-catalog" && <PageProductCatalogDashboard setPage={setPage} />}
          {page==="sales-wb"       && <PageSalesWaterBased fgs={fgs} setPage={setPage} />}
          {page==="sales-hm"       && <PageSalesHotMelt setPage={setPage} />}
          {page==="sales-work-orders" && (
            <PageWorkOrders
              myPerms={myPerms}
              userEmail={userEmail}
              userName={userName}
              fgs={fgs}
              hmGrades={hmGrades}
            />
          )}
          {page==="sales-quotations" && (
            <PageQuotations
              myPerms={myPerms}
              userEmail={userEmail}
              fgs={fgs}
              sfgs={sfgs}
              hmGrades={hmGrades}
              companies={crmCompanies}
              userName={userName}
            />
          )}
          {page==="sales-proforma" && (
            <PageProformaInvoices
              myPerms={myPerms}
              userEmail={userEmail}
              fgs={fgs}
              hmGrades={hmGrades}
              userName={userName}
            />
          )}
          {page==="sales-soa"            && <PageSOAPaymentFollowUp myPerms={myPerms} userEmail={userEmail} />}
          {page==="sales-visit-reports"  && <PageVisitReports myPerms={myPerms} userEmail={userEmail} />}
          {page==="sales-monthly-reports"&& <PageMonthlyReports myPerms={myPerms} userEmail={userEmail} />}
          {page==="sales-targets"           && <PageSalesTargets myPerms={myPerms} userEmail={userEmail} />}
          {page==="sales-customer-samples" && <PageSalesCustomerSampleSubmissions userEmail={userEmail} />}
          {page==="production"     && <PageProductionDashboard setPage={setPage} />}
          {page==="maintenance"    && <PageMaintenanceDashboard setPage={setPage} />}
          {page==="prod-equipment" && <div className="alert-info" style={{ padding:24 }}>Equipment — coming soon.</div>}
          {page==="prod-backlog"   && <div className="alert-info" style={{ padding:24 }}>Production Backlog — coming soon.</div>}
          {page==="maint-spares"   && <div className="alert-info" style={{ padding:24 }}>Spare Parts List — coming soon.</div>}
          {page==="maint-planned"  && <div className="alert-info" style={{ padding:24 }}>Planned Maintenance Logs — coming soon.</div>}
          {page==="hr"             && <PageHRDashboard setPage={setPage} />}
          {page==="hr-employees"   && <div className="alert-info" style={{ padding:24 }}>Employee Records Management — coming soon.</div>}
          {page==="hr-payroll"     && <div className="alert-info" style={{ padding:24 }}>Payroll & WPS — coming soon.</div>}
          {page==="hr-documents"   && <div className="alert-info" style={{ padding:24 }}>Document Expiry Reminders — coming soon.</div>}
          {page==="hr-self-service"&& <div className="alert-info" style={{ padding:24 }}>Employee Self-Service Portal — coming soon.</div>}
          {page==="hr-reports"     && <div className="alert-info" style={{ padding:24 }}>HR Reports — coming soon.</div>}
          {page==="rmcalc"    && <PageRMCalc rms={rms} sfgs={sfgs} sfgFormulas={sfgFormulas} fgs={fgs} fgFormulas={fgFormulas} />}
          {page==="users"         && <PageUsers />}
          {page==="bank-accounts" && <PageBankAccounts companies={crmCompanies} />}
          {page==="infra-keys"   && <PageInfraKeys />}
          {page==="mixer-config" && <PageMixerConfig myPerms={myPerms} />}
        </>}
      </div>

      {idleWarning && (
        <div className="overlay">
          <div className="modal" style={{ maxWidth:380, textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:8 }}>⏱</div>
            <h3 style={{ marginBottom:8 }}>Still there?</h3>
            <p style={{ color:"#6b7280", fontSize:14, marginBottom:20 }}>
              You've been inactive for 4 minutes.<br/>
              You'll be signed out automatically in <strong>1 minute</strong>.
            </p>
            <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
              <button className="btn" onClick={signOut}>Sign Out Now</button>
              <button className="btn btn-primary" onClick={() => {
                lastActivityRef.current = Date.now();
                setIdleWarning(false);
              }}>Stay Logged In</button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => { if (e.target===e.currentTarget) setModal(null); }}>
          {modal.type==="sfgForm"   && <SFGFormModal  sfg={modal.data} sfgFormulas={sfgFormulas} rms={rms} sfgCost={sfgCost} fgs={fgs} fgFormulas={fgFormulas} onEdit={sfg => setModal({type:"editSFG", data:sfg})} onClose={() => setModal(null)} />}
          {modal.type==="fgForm"    && <FGFormModal   fg={modal.data} fgFormulas={fgFormulas} sfgFormulas={sfgFormulas} rms={rms} sfgs={sfgs} sfgCost={sfgCost} fgContent={fgContent} packBreak={packBreak} settings={settings} onEdit={fg => setModal({type:"editFG", data:fg})} onClose={() => setModal(null)} />}
          {modal.type==="costCell"  && <CostCellModal fg={modal.data.fg} pkg={modal.data.pkg} packBreak={packBreak} customPct={settings.customPct} labourPerKg={settings.labourPerKg} wastageGlobal={settings.wastageGlobal??1} onClose={() => setModal(null)} />}
          {modal.type==="addRM"     && <AddRMModal    onSave={handleAddRM} onCancel={() => setModal(null)} />}
          {modal.type==="addRMSpec" && <RmSpecParamModal rmId={modal.data.id} param={null} onSave={f => handleAddRMSpec(modal.data.id, f)} onCancel={() => setModal(null)} mandatory />}
          {modal.type==="rmHist"    && <RMHistModal   rm={modal.data} priceHist={priceHist} onUpdate={handleRMPriceUpdate} onClose={() => setModal(null)} />}
          {modal.type==="addSFG"    && <AddSFGModal   onSave={handleAddSFG} onCancel={() => setModal(null)} />}
          {modal.type==="addSFGSpec"&& <FgSpecModal   fgId={modal.data.id} spec={null} fgsWithoutSpec={[]} onSave={handleAddSFGSpec} onCancel={() => setModal(null)} mandatory />}
          {modal.type==="editSFG"   && <EditSFGModal  sfg={modal.data} rms={rms} formula={sfgFormulas[modal.data.id]||[]} onSave={handleSaveSFGFormula} onCancel={() => setModal(null)} />}
          {modal.type==="addFG"     && <AddFGModal    onSave={handleAddFG} existingIds={fgs.map(f => f.id)} onCancel={() => setModal(null)} />}
          {modal.type==="addFGSpec" && <FgSpecModal   fgId={modal.data.id} spec={null} fgsWithoutSpec={[]} onSave={handleAddFGSpec} onCancel={() => setModal(null)} mandatory />}
          {modal.type==="editFG"    && <EditFGModal   fg={modal.data} rms={rms} sfgs={sfgs} formula={fgFormulas[modal.data.id]||[]} onSave={handleSaveFGFormula} onCancel={() => setModal(null)} />}
        </div>
      )}

      <Watermark email={userEmail} />
    </div>
    </ToastProvider>
  );
}
